import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { bannerAPI, handleApiError, validateBannerForm } from "../../utils/api";

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBannerId, setCurrentBannerId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const initialFormState = {
    title: "",
    imageUrl: "",
    altText: "",
    link: "",
    category: "games",
    isActive: true,
    order: 0,
  };

  const [formData, setFormData] = useState(initialFormState);

  // Fetch banners from API
  const fetchBanners = async () => {
    setLoading(true);
    try {
      const data = await bannerAPI.admin.getBanners();
      setBanners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching banners:", error);
      setSnackbar({
        open: true,
        message: handleApiError(error, "Failed to fetch banners"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleOpen = () => {
    setEditMode(false);
    setFormData({
      ...initialFormState,
      order: banners.length + 1,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormState);
    setCurrentBannerId(null);
  };

  const handleEdit = (banner) => {
    setEditMode(true);
    setCurrentBannerId(banner._id);
    setFormData({
      title: banner.title || "",
      imageUrl: banner.imageUrl || "",
      altText: banner.altText || "",
      link: banner.link || "",
      category: banner.category || "games",
      isActive: banner.isActive !== undefined ? banner.isActive : true,
      order: banner.order || 0,
    });
    setOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "isActive" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Explicitly construct the payload to ensure all required fields are present and correctly formatted.
    // This prevents any potential issues with extra fields or incorrect types being sent to the backend.
    const payload = {
      title: (formData.title || "").trim(),
      imageUrl: (formData.imageUrl || "").trim(),
      altText: (formData.altText || "").trim(),
      link: (formData.link || "").trim(),
      category: formData.category || "games",
      isActive: Boolean(formData.isActive),
      order: Number(formData.order) || 0,
    };

    console.log("Submitting banner with payload:", payload);

    // Validate the cleaned payload instead of raw formData
    const { isValid, errors } = validateBannerForm(payload);
    if (!isValid) {
      console.error("Banner validation failed locally:", errors);
      setSnackbar({
        open: true,
        message: `Validation Error: ${Object.values(errors).join(", ")}`,
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      if (editMode) {
        console.log(`Updating banner ${currentBannerId}...`);
        await bannerAPI.admin.updateBanner(currentBannerId, payload);
        setSnackbar({
          open: true,
          message: "Banner updated successfully",
          severity: "success",
        });
      } else {
        console.log("Creating new banner...");
        await bannerAPI.admin.createBanner(payload);
        setSnackbar({
          open: true,
          message: "Banner added successfully",
          severity: "success",
        });
      }

      await fetchBanners();
      handleClose();
    } catch (error) {
      console.error("Error saving banner:", error);
      setSnackbar({
        open: true,
        message: handleApiError(
          error,
          `Failed to ${editMode ? "update" : "add"} banner`,
        ),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;

    setLoading(true);
    try {
      await bannerAPI.admin.deleteBanner(id);
      setSnackbar({
        open: true,
        message: "Banner deleted successfully",
        severity: "success",
      });
      fetchBanners();
    } catch (error) {
      console.error("Error deleting banner:", error);
      setSnackbar({
        open: true,
        message: handleApiError(error, "Failed to delete banner"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (banner) => {
    try {
      await bannerAPI.admin.updateBanner(banner._id, {
        isActive: !banner.isActive,
      });
      setSnackbar({
        open: true,
        message: `Banner ${!banner.isActive ? "activated" : "deactivated"} successfully`,
        severity: "success",
      });
      fetchBanners();
    } catch (error) {
      setSnackbar({
        open: true,
        message: handleApiError(error, "Failed to update status"),
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Banner Management
        </Typography>
        <Box>
          <IconButton onClick={fetchBanners} disabled={loading} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            disabled={loading}
          >
            Add New Banner
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: "action.hover" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Order</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Preview</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Title & Info</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && banners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : banners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    No banners found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              banners.map((banner) => (
                <TableRow key={banner._id} hover>
                  <TableCell>{banner.order}</TableCell>
                  <TableCell>
                    <Box
                      component="img"
                      src={banner.imageUrl}
                      alt={banner.altText}
                      sx={{
                        width: 120,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {banner.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Alt: {banner.altText}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ wordBreak: "break-all" }}
                    >
                      Link: {banner.link}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {banner.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={banner.isActive ? "Deactivate" : "Activate"}
                    >
                      <IconButton
                        onClick={() => handleToggleStatus(banner)}
                        color={banner.isActive ? "success" : "default"}
                      >
                        {banner.isActive ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(banner)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(banner._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? "Edit Banner" : "Add New Banner"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              required
              fullWidth
              label="Banner Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              required
              fullWidth
              label="Image URL"
              name="imageUrl"
              placeholder="https://example.com/banner.jpg"
              value={formData.imageUrl}
              onChange={handleInputChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              required
              fullWidth
              label="Alt Text (SEO)"
              name="altText"
              value={formData.altText}
              onChange={handleInputChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              required
              fullWidth
              label="Target URL (Link)"
              name="link"
              placeholder="/games/mobile-legends"
              value={formData.link}
              onChange={handleInputChange}
              variant="outlined"
            />

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  label="Category"
                >
                  <MenuItem value="games">Games</MenuItem>
                  <MenuItem value="giftcards">Gift Cards</MenuItem>
                  <MenuItem value="promotions">Promotions</MenuItem>
                </Select>
              </FormControl>

              <TextField
                type="number"
                label="Order"
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                inputProps={{ min: 0 }}
                variant="outlined"
                sx={{ width: "120px" }}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  name="isActive"
                  color="primary"
                />
              }
              label="Active Status"
              sx={{ mt: 2 }}
            />

            {formData.imageUrl && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Preview:
                </Typography>
                <Box
                  component="img"
                  src={formData.imageUrl}
                  sx={{
                    width: "100%",
                    height: "auto",
                    mt: 1,
                    borderRadius: 1,
                    border: "1px solid #ddd",
                  }}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/800x400?text=Invalid+Image+URL";
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              {editMode ? "Update Banner" : "Save Banner"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BannerManager;
